import apiClient from '../client'
import type { AgentStatus } from '@/types'

export interface DashboardStats {
  total_leads: number
  active_leads: number
  new_leads_today: number
  total_calls: number
  inbound_calls: number
  outbound_calls: number
  total_emails: number
  inbound_emails: number
  outbound_emails: number
  conversion_rate: number
  scouting_activities: number
  avg_response_time: number
}

export interface ScoutingBreakdown {
  status_counts: Record<string, number>
  source_counts: Record<string, number>
  daily_counts: Array<{ date: string; count: number }>
}

export interface RecentLead {
  id: string
  company_name: string
  contact_name: string | null
  status: string
  created_at: string
}

export interface RecentActivity {
  id: string
  lead_id: string | null
  activity_type: string
  channel: string | null
  direction: string | null
  subject: string | null
  outcome: string | null
  notes: string | null
  details: Record<string, unknown> | null
  metadata_: Record<string, unknown> | null
  created_at: string
  prompt_usage?: Array<{
    prompt_type: string
    prompt_version: string
    outcome: string
    outcome_score: number
  }>
}

export interface FeatureFlags {
  [key: string]: boolean
}

export const dashboardApi = {
  // Stats/Metrics
  getMetrics: () =>
    apiClient.get<DashboardStats>('/api/dashboard/metrics'),

  // Alias for backwards compatibility
  getStats: () =>
    apiClient.get<DashboardStats>('/api/dashboard/metrics'),

  getScoutingBreakdown: () =>
    apiClient.get<ScoutingBreakdown>('/api/dashboard/scouting/breakdown'),

  // Recent leads - uses the leads/recent endpoint with limit
  getRecentLeads: (limit: number = 5) =>
    apiClient.get<{ leads: RecentLead[] }>('/api/dashboard/leads/recent', { params: { limit } }),

  // Recent activities - uses the activities/recent endpoint with limit
  getRecentActivities: (limit: number = 10) =>
    apiClient.get<{ activities: RecentActivity[] }>('/api/dashboard/activities/recent', { params: { limit } }),

  // Agent status
  getAgentStatus: () =>
    apiClient.get<AgentStatus[]>('/api/dashboard/agents/status'),

  // Feature flags
  getFeatureFlags: () =>
    apiClient.get<{ flags: Record<string, boolean> }>('/api/dashboard/features'),

  updateFeatureFlag: (featureName: string, enabled: boolean) =>
    apiClient.post<{ success: boolean }>(`/api/dashboard/features/${featureName}`, { enabled }),

  // Health
  getHealth: () =>
    apiClient.get<{ status: string; database: string; environment: string }>('/api/dashboard/health'),
}
